import { language } from '../LanguageService';
import { AsperaError } from '../../classes/AsperaError';
import { deepClone, getStorageItem } from '../../helpers/starterHelpers';
import { getTransferSystemStatus } from '../../helpers/transfer';
import { Subject, ReplaySubject } from 'rxjs';
import { storageKeys } from '../../constants/constants';
import { dirtyFormConfirmation } from '../../helpers/components';
import { setBranding } from '@ibm-aspera/sdk';
/**
 * The systems that track the user's use of the system. This handles Segmentation and NPS
 */
export class TrackingService {
    constructor() {
        /** Indicate that normal theme setting is blocked. Use forceSetCustomTheme for parent apps. */
        this.disableNormalCustomTheme = false;
        /** Indicates if user activity has occurred recently for NPS tracking */
        this.npsUserActivity = false;
        /** Indicates if user activity has occurred recently for session tracking */
        this.userActivity = false;
        /** Timestamp user was last active */
        this.userLastActive = new Date();
        /** Last user session data */
        this.lastUserSessionData = {};
        /** Current page and app English info */
        this.currentPageData = { appName: 'HOME', pageName: 'ROOT' };
        /** Indicates if NPS has already been setup */
        this.npsRunning = false;
        /** Store for user preferences */
        this.userPreference = {};
        this.userPreferencesSub = new Subject();
        this.mobilePromptFlow = new ReplaySubject(1);
        this.themeChangeSub = new ReplaySubject(1);
        this.currentThemeInternal = 'g10';
        this.customThemeChangeSub = new Subject();
        this.dirtyForms = new Map();
        this.userSessionSubject = new Subject();
        this.desktopOpenSubject = new Subject();
        this.reloadTableSubject = new Subject();
        window.addEventListener('click', () => {
            this.userActivity = true;
            this.npsUserActivity = true;
            this.userLastActive = new Date();
        });
        window.addEventListener('touchstart', () => {
            this.userActivity = true;
            this.npsUserActivity = true;
            this.userLastActive = new Date();
        });
        window.addEventListener('keypress', () => {
            this.userActivity = true;
            this.npsUserActivity = true;
            this.userLastActive = new Date();
        });
        window.addEventListener('scroll', () => {
            this.userActivity = true;
            this.userLastActive = new Date();
        });
    }
    /**
     * Store tracking data in window memory for debugging tracking service
     * @param data - any data to store in array (avoid classes)
     */
    trackingFail(data) {
        try {
            if (!Array.isArray(window.trackingFailures)) {
                window.trackingFailures = [];
            }
            window.trackingFailures.push(data);
        }
        catch (error) {
            new AsperaError('TrackingService: failed to store data into tracking fail tracker', error);
        }
    }
    /**
     * Monitor if user has been idle for an extended time and should be allowed for trigger based NPS
     */
    monitorForUserTrigger() {
        const idleTesting = () => {
            setTimeout(() => {
                if (!this.npsUserActivity) {
                    window.IBM_Meta.trigger1 = true;
                    this.triggerNpsChange();
                }
                else {
                    this.npsUserActivity = false;
                    idleTesting();
                }
            }, 120000);
        };
        idleTesting();
    }
    /**
     * Get the current session time in seconds (time since tracking was started; which should happen at page load)
     */
    get sessionTime() {
        if (!this.sessionStartTime)
            return 0;
        const now = new Date();
        const nowTime = Math.round(now.getTime() / 1000);
        return nowTime - this.sessionStartTime;
    }
    internallySetTheme(theme) {
        if (!this.allowLightDarkModes) {
            return;
        }
        this.originalThemeBeforeDisable = undefined;
        switch (theme) {
            case 'DARK':
                this.currentTheme = 'g100';
                break;
            case 'LIGHT':
                this.currentTheme = 'g10';
                break;
            case 'NONE':
            default:
                this.currentTheme = this.systemTheme;
                break;
        }
    }
    /**
     * Set internal custom theme
     *
     * @param theme - custom theme to set
     */
    setInternalCustomTheme(theme) {
        this.currentCustomThemeInternal = theme;
        if (theme === null || theme === void 0 ? void 0 : theme.disableLightDarkMode) {
            if (!this.originalThemeBeforeDisable) {
                this.originalThemeBeforeDisable = this.currentThemeInternal;
            }
            this.currentThemeInternal = 'g10';
        }
        else if (this.originalThemeBeforeDisable) {
            this.currentThemeInternal = this.originalThemeBeforeDisable;
        }
        /** Buttons are unique and will auto fill Carbon tokens from the core tokens */
        if (theme) {
            const setupSpecialTokens = (colorTheme) => {
                if (colorTheme.buttonPrimaryBackground) {
                    colorTheme['$button-primary'] = colorTheme.buttonPrimaryBackground;
                }
                if (colorTheme.buttonPrimaryBackgroundHover) {
                    colorTheme['$button-primary-hover'] = colorTheme.buttonPrimaryBackgroundHover;
                }
                if (colorTheme.buttonPrimaryBackgroundActive) {
                    colorTheme['$button-primary-active'] = colorTheme.buttonPrimaryBackgroundActive;
                }
                if (colorTheme.buttonSecondaryBackground) {
                    colorTheme['$button-secondary'] = colorTheme.buttonSecondaryBackground;
                }
                if (colorTheme.buttonSecondaryBackgroundHover) {
                    colorTheme['$button-secondary-hover'] = colorTheme.buttonSecondaryBackgroundHover;
                }
                if (colorTheme.buttonSecondaryBackgroundActive) {
                    colorTheme['$button-secondary-active'] = colorTheme.buttonSecondaryBackgroundActive;
                }
                if (colorTheme.buttonTertiaryBorder) {
                    colorTheme['$button-tertiary'] = colorTheme.buttonTertiaryBorder;
                }
                if (colorTheme.buttonTertiaryBackgroundHover) {
                    colorTheme['$button-tertiary-hover'] = colorTheme.buttonTertiaryBackgroundHover;
                }
                if (colorTheme.buttonTertiaryBackgroundActive) {
                    colorTheme['$button-tertiary-active'] = colorTheme.buttonTertiaryBackgroundActive;
                }
            };
            if (theme.g10) {
                setupSpecialTokens(theme.g10);
            }
            if (theme.g100) {
                setupSpecialTokens(theme.g100);
            }
        }
        this.customThemeChangeSub.next(true);
        this.themeChangeSub.next(this.currentThemeInternal);
    }
    /** Indicate NPS update (usually page change or trigger change) */
    triggerNpsChange() {
        if (window.KAMPYLE_ONSITE_SDK && typeof window.KAMPYLE_ONSITE_SDK.updatePageView === 'function') {
            window.KAMPYLE_ONSITE_SDK.updatePageView();
        }
    }
    setCurrentPage(currentAppNameKey, currentPageNameKey) {
        if (!this.segmentationData)
            return;
        const priorPage = this.currentPageData.pageName;
        const priorApp = this.currentPageData.appName;
        const appLanguageKeys = {
            AOC: 'globals.aocTitle',
            AOC_ON_PREM: 'globals.aocOnPremTitle',
            ENTERPRISE: 'globals.asperaTitle',
            TEST: 'globals.testTitle',
            MOBILE: 'globals.mobileTitle',
            CONNECT: 'globals.connectTitle',
            CONNECTOR: 'globals.connectorTitle',
            DESKTOP: 'globals.desktopTitle',
        };
        window.document.title = language.get(appLanguageKeys[this.segmentationData.productKey], [language.get(currentAppNameKey), language.get(currentPageNameKey)]);
        this.currentPageData = {
            appName: language.get(currentAppNameKey, undefined, true),
            pageName: language.get(currentPageNameKey, undefined, true),
        };
        const eventData = {
            successFlag: true,
            title: this.currentPageData.pageName,
            referringPageName: priorPage,
            referringPageCategory: priorApp,
        };
        this.track('Loaded a Page', eventData);
    }
    /**
     * This function is used to track application events
     *
     * @param eventName - the name of the event to be sent
     * @param eventData - the tracking data to send (provided by Segmentation coordinator. Request new items be supported in AsperaStarter)
     */
    track(eventName, eventData) {
        var _a;
        if (!this.segmentationData || !(window.bluemixAnalytics && typeof window.bluemixAnalytics.trackEvent === 'function')) {
            this.trackingFail({ eventName, eventData });
            return;
        }
        const transferData = getTransferSystemStatus();
        let transferSystem;
        if (transferData.type === 'http')
            transferSystem = 'Aspera HTTP Gateway';
        if (transferData.type === 'connect')
            transferSystem = 'Aspera Connect';
        if (transferData.type === 'desktop')
            transferSystem = 'Aspera desktop';
        if (transferData.type === 'mobile')
            transferSystem = 'Aspera Mobile';
        if (window.walkmeData) {
            window.walkmeData.connectRunning = transferData.connectAvailable;
            window.walkmeData.httpGatewayAvailable = !!transferData.httpServerUrl;
        }
        if (eventName === 'Started Process' && !eventData.processType) {
            eventData.processType = 'event';
        }
        if (eventData.object && !eventData.objectType) {
            eventData.objectType = eventData.object;
        }
        if (eventName.includes('Object') && !eventData.objectType) {
            eventData.objectType = 'object';
        }
        const id = this.segmentationData.currentUser.ibmId || `${this.segmentationData.currentUser.realm}-${this.segmentationData.currentUser.id}` || '';
        const internalData = {
            'custom.transferSystem': transferSystem,
            'custom.productKey': this.segmentationData.productKey,
            'custom.connectStatus': transferData.connectStatus,
            'custom.connectAvailable': transferData.connectAvailable,
            'custom.connectVersion': transferData.connectAppVersion,
            'custom.httpGatewayVersion': transferData.httpVersion,
            productTitle: this.segmentationData.productTitle,
            parentPageCategory: this.currentPageData.appName,
            parentPageName: this.currentPageData.pageName,
            url: window.location.href,
            path: window.location.pathname,
            'custom.defaultLanguage': language.getCurrentLanguage().code,
            'custom.apiId': this.segmentationData.currentUser.id,
            'custom.appVersion': this.segmentationData.appVersion,
            accountPlan: this.segmentationData.currentAccount.trial ? 'trial' : (_a = this.segmentationData) === null || _a === void 0 ? void 0 : _a.currentAccount.plan,
            accountName: this.segmentationData.currentAccount.name,
            'custom.trial_expires_at': this.segmentationData.currentAccount.trialExpireDate,
            'custom.sessionTime': this.sessionTime,
            'user.email': this.segmentationData.currentUser.email,
            'user.role': this.segmentationData.currentUser.role,
            UT30: this.segmentationData.UT30,
            productCode: this.segmentationData.productCode,
            productCodeType: this.segmentationData.productCodeType,
            instanceId: this.segmentationData.instanceId,
            userId: id,
        };
        const extraData = deepClone(this.appendExtraTrackingData ? this.appendExtraTrackingData() : {});
        const clonedEventData = deepClone(eventData || {});
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const dataToSend = Object.assign(internalData, clonedEventData, extraData);
        // Move custom data to top level
        Object.keys(dataToSend.custom || {}).forEach(key => {
            dataToSend[`custom.${key}`] = dataToSend.custom[key];
        });
        if (typeof dataToSend.successFlag === 'boolean')
            dataToSend['custom.successFlag'] = dataToSend.successFlag;
        delete dataToSend.custom;
        window.bluemixAnalytics.trackEvent(eventName, dataToSend);
    }
    /**
     * Setup Segmentation support (includes tracking user).
     * This will also identify the user on the platform
     *
     * @param data - the data to set for segmentation
     * @param addExtraDataCallback - callback that takes an object of data to include on the track call.  This is useful for app specific features like Workspace and app specific fields.
     */
    initSegmentation(data, addExtraDataCallback) {
        if (this.segmentationData)
            return;
        this.segmentationData = data;
        const now = new Date();
        this.sessionStartTime = Math.round(now.getTime() / 1000);
        if (typeof addExtraDataCallback === 'function')
            this.appendExtraTrackingData = addExtraDataCallback;
        if (window.analytics && typeof window.analytics.identify === 'function') {
            const id = data.currentUser.ibmId || `${data.currentUser.realm}-${data.currentUser.id}` || '';
            const identifyData = {
                realmName: data.currentUser.realm,
                uniqueSecurityName: id.split('-')[1],
                email: data.currentUser.email,
                id,
                createdAt: data.currentUser.createdAt,
                firstName: data.currentUser.firstName,
                lastName: data.currentUser.lastName,
                name: (data.currentUser.firstName && data.currentUser.lastName) ? `${data.currentUser.firstName} ${data.currentUser.lastName}` : data.currentUser.email,
                accountPlan: data.currentAccount.trial ? 'trial' : data.currentAccount.plan,
                accountName: data.currentAccount.name,
            };
            if (this.segmentationData.anonymousId) {
                window.analytics.identify({
                    subscriptionStatus: 'inactive',
                });
            }
            else {
                window.analytics.identify(id, identifyData);
            }
        }
        const transferData = getTransferSystemStatus();
        window.walkmeData = {
            admin: !!data.currentUser.admin,
            samlUser: !data.currentUser.ibmId,
            connectRunning: transferData.connectAvailable,
            httpGatewayAvailable: !!transferData.httpServerUrl,
        };
    }
    /** Indicate if NPS has been started */
    get isNpsRunning() {
        return this.npsRunning;
    }
    /** Indicate if segment has been started */
    get isSegmentRunning() {
        return !!this.segmentationData;
    }
    /**
     * Setup NPS for random NPS collections.
     * Language will be set for you based on current session unless specified on call.
     *
     * @param data - the data to set for NPS
     */
    initNps(data) {
        if (this.npsRunning)
            return;
        window.IBM_Meta = data;
        window.IBM_Meta.language = data.language || language.getCurrentLanguage().code;
        window.IBM_Meta.trigger1 = false;
        window.IBM_Meta.trigger2 = false;
        window.IBM_Meta.trigger3 = false;
        window.IBM_Meta.trigger4 = false;
        window.IBM_Meta.trigger5 = false;
        window.IBM_Meta.trigger6 = false;
        window.IBM_Meta.trigger7 = false;
        window.IBM_Meta.trigger8 = false;
        window.IBM_Meta.trigger9 = false;
        window.IBM_Meta.trigger10 = false;
        this.npsRunning = true;
        if (window.IBM_Meta.triggerIntercept)
            this.monitorForUserTrigger();
    }
    /** Subscribe to watch for session updated. This is called EVERY second. */
    get sessionUpdateWatcher() {
        return this.userSessionSubject.asObservable();
    }
    /**
     * Set the last user session data.
     *
     * @param data - The session data to set
     */
    setLastUserSessionData(data) {
        this.lastUserSessionData = Object.assign(Object.assign({}, data), { lastReceived: new Date().getTime() });
    }
    /**
     * Init session monitoring.
     * `activityCallback` should call `setLastUserSessionData` when it has new data.
     *
     * @param activityCallback - callback that is called when activity is recorded. This is done every minute
     */
    initSessionMonitor(activityCallback) {
        this.userSessionActivityCallback = activityCallback;
        const activityTesting = () => {
            setTimeout(() => {
                if (this.userActivity) {
                    this.userActivity = false;
                    if (typeof this.userSessionActivityCallback === 'function') {
                        this.userSessionActivityCallback();
                    }
                }
                activityTesting();
            }, 45000);
        };
        activityTesting();
        if (typeof this.userSessionActivityCallback === 'function') {
            this.userSessionActivityCallback();
        }
        const activityUpdates = () => {
            setTimeout(() => {
                if (this.lastUserSessionData.tokenExpires || this.lastUserSessionData.inactivityTimeout) {
                    const now = new Date().getTime();
                    const secondsSinceLastActivity = (now - this.userLastActive.getTime()) / 1000;
                    let secondsSinceLastReceived = 0;
                    if (this.lastUserSessionData.lastReceived) {
                        secondsSinceLastReceived = (now - (this.lastUserSessionData.lastReceived || 0)) / 1000;
                    }
                    this.userSessionSubject.next({
                        tokenExpires: this.lastUserSessionData.tokenExpires ? this.lastUserSessionData.tokenExpires - secondsSinceLastReceived : undefined,
                        inactivityExpiresIn: this.lastUserSessionData.inactivityTimeout ? this.lastUserSessionData.inactivityTimeout - secondsSinceLastActivity : undefined,
                        inactivityTimeout: this.lastUserSessionData.inactivityTimeout,
                        lastReceived: this.lastUserSessionData.lastReceived,
                        lastReceivedTokenExpires: this.lastUserSessionData.tokenExpires,
                    });
                }
                activityUpdates();
            }, 10000);
        };
        activityUpdates();
    }
    /**
     * Set user preferences. This only sets values for the keys present
     *
     * @param preference - the partial user preference options.  Only what is set (has key for) will be set.
     * @param updateApi - indicate if the API should be updated (call updateUserPreferences)
     * @param requestReload - indicate if the page should be reloaded after API update (handled on the app side)
     */
    setUserPreferences(preferences, updateApi, requestReload) {
        Object.keys(preferences).forEach(key => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            this.userPreference[key] = preferences[key];
        });
        if (preferences.darkLightMode) {
            this.internallySetTheme(preferences.darkLightMode);
        }
        if (updateApi)
            this.updateUserPreferences(this.userPreference, requestReload);
    }
    /**
     * Get a user preference item for a specific item.
     *
     * @param preference - the key of the preference to get
     *
     * @returns the string or number for that preference. If not set undefined is returned.
     */
    getUserPreference(preference) {
        return this.userPreference[preference];
    }
    /**
     * Use this for force setting. Use when disableNormalCustomTheme is true.
     * As currentCustomTheme is disabled when that flag is on
     *
     * @param theme - custom theme
     */
    forceSetCustomTheme(theme) {
        this.setInternalCustomTheme(theme);
    }
    /**
     * Observable to monitor user preference updates.
     * This indicates that the API should be updated with the new user preferences.
     */
    get userPreferencesUpdater() {
        return this.userPreferencesSub.asObservable();
    }
    /**
     * Subscribe to custom theme changes
     */
    get customThemeChanges() {
        return this.customThemeChangeSub.asObservable();
    }
    /** The custom theme. For supporting only custom theme set disableLightDarkMode to false and set same theme on both g10 and g100. */
    get currentCustomTheme() {
        return this.currentCustomThemeInternal;
    }
    /** The custom theme. For supporting only custom theme set disableLightDarkMode to false and set same theme on both g10 and g100. */
    set currentCustomTheme(theme) {
        if (this.disableNormalCustomTheme) {
            return;
        }
        this.setInternalCustomTheme(theme);
    }
    /**
     *
     * @param id - ID of the theme (PRODUCT-ORG_ID-WS_ID)
     * @param name - Name of the theme (Product - Workspace Name | Product)
     * @param theme - The theme
     */
    setCurrentCustomThemeWithId(id, name, theme) {
        if (getTransferSystemStatus().type === 'desktop' && theme && Object.keys(theme).length) {
            setBranding(id, { name, theme }).then(() => { }).catch(() => { });
        }
        this.currentCustomTheme = theme;
    }
    get allowLightDarkModes() {
        var _a;
        return !((_a = this.currentCustomThemeInternal) === null || _a === void 0 ? void 0 : _a.disableLightDarkMode);
    }
    /**
     * Subscribe to theme changes
     */
    get themeChange() {
        return this.themeChangeSub.asObservable();
    }
    /** The current theme of the application (light=g10/dark=g100) */
    get currentTheme() {
        return this.currentThemeInternal;
    }
    /** The current theme of the application (light=g10/dark=g100). This will also trigger CustomTheming updates */
    set currentTheme(theme) {
        this.currentThemeInternal = theme;
        this.themeChangeSub.next(theme);
        this.customThemeChangeSub.next(true);
    }
    /** The current theme of the application (NONE=SYSTEM, LIGHT, DARK) */
    get themeChoice() {
        return this.userPreference.darkLightMode || 'NONE';
    }
    /** The current theme of the application (NONE=SYSTEM, LIGHT, DARK) */
    set themeChoice(theme) {
        this.setUserPreferences({ darkLightMode: theme }, true);
        this.internallySetTheme(theme);
    }
    /** The current theme of the system */
    get systemTheme() {
        const storageChoice = getStorageItem(storageKeys.darkMode);
        if (storageChoice === 'DARK') {
            return 'g100';
        }
        else if (!storageChoice && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
            return 'g100';
        }
        return 'g10';
    }
    /** Mobile prompt observable to watch for changes by AsperaMobileInstaller */
    get mobilePromptLauncher() {
        return this.mobilePromptFlow.asObservable();
    }
    /**
     * If the mobile app supports this device and system will prompt user. If not will instantly call callback
     * @param callback - callback to call if the user dismisses the installer (or instant callback if not mobile)
     */
    triggerMobilePromptLauncher(callback) {
        this.mobilePromptFlow.next({ callback });
    }
    /**
     * Update user preferences locally and notify app to update on the API
     *
     * @param preferences - the partial user preference options.  Only what is set (has key for) will be set.
     * @param requestReload - indicate if the page should be reloaded after API update (handled on the app side)
     */
    updateUserPreferences(preferences, requestReload) {
        this.setUserPreferences(preferences);
        this.userPreferencesSub.next({ preferences, reload: !!requestReload });
    }
    /**
     * Mark a form as dirty
     *
     * @param formId - the ID of the form
     */
    setDirtyForm(formId) {
        this.dirtyForms.set(formId, true);
    }
    /**
     * Indicate a form is destroyed and no longer dirty
     * This should be called by willUnmount on AsperaForm
     *
     * @param formId - the ID of the form
     */
    removeDirtyForm(formId) {
        this.dirtyForms.delete(formId);
    }
    /**
     * Reset all form storage and clear all dirty forms
     */
    resetAllDirtyForms() {
        this.dirtyForms.clear();
    }
    /** Toggle Assist Me if loaded */
    toggleAssistMe() {
        if (window.assistMeController && typeof window.assistMeController.close === 'function' && typeof window.assistMeController.open === 'function' && typeof window.assistMeController.isOpen === 'function') {
            if (window.assistMeController.isOpen()) {
                window.assistMeController.close((error) => {
                    if (error) {
                        new AsperaError('toggleAssistMe: Assist me returned an error trying to close', error);
                    }
                });
            }
            else {
                window.assistMeController.open((error) => {
                    if (error) {
                        new AsperaError('toggleAssistMe: Assist me returned an error trying to open', error);
                    }
                });
            }
        }
    }
    /** Close Assist Me if loaded */
    closeAssistMe() {
        if (window.assistMeController && typeof window.assistMeController.close === 'function') {
            window.assistMeController.close((error) => {
                if (error) {
                    new AsperaError('closeAssistMe: Assist me returned an error trying to close', error);
                }
            });
        }
    }
    /**
     * Safely check if a dirty form is rendered and prompt user if
     * they want to leave or not.
     *
     * @param callback - Callback to call if user wants to continue to navigate or no blocking is needed
     * @param rightPanelClose - Indicate if right panel is being closed
     */
    checkDirtyForms(callback, rightPanelClose) {
        if (this.dirtyForms.size) {
            let querySelector = '';
            if (rightPanelClose) {
                this.dirtyForms.forEach((_item, key) => {
                    querySelector += `.aspera-slide-over-panel #AsperaForm-${key}, `;
                });
            }
            else {
                this.dirtyForms.forEach((_item, key) => {
                    querySelector += `#AsperaForm-${key}, `;
                });
            }
            querySelector = querySelector.substring(0, querySelector.length - 2);
            const foundItem = document.querySelector(querySelector);
            if (foundItem) {
                dirtyFormConfirmation(callback);
            }
            else {
                callback();
            }
        }
        else {
            callback();
        }
    }
    get desktopOpenRequest() {
        return this.desktopOpenSubject.asObservable();
    }
    /**
     * Request desktop installer to open
     */
    openDesktopInstaller() {
        this.desktopOpenSubject.next(true);
    }
    get reloadTables() {
        return this.reloadTableSubject.asObservable();
    }
    /**
     * Request tables to reload across the app
     */
    requestAllTableReload() {
        this.reloadTableSubject.next(true);
    }
}
/** The shared tracking service */
export const tracking = new TrackingService();
